import { useContext, useEffect, useMemo, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  Account,
  AccountCollection,
  AccountSubTypes,
  AccountType,
  ClientCollection,
  ClientContribution,
  ClientContributionCollection,
  Contribution,
} from "wealthmode-frontend/backend"
import {
  Box,
  HStack,
  Heading,
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
  Spinner,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"
import { ChevronDownIcon } from "wealthmode-frontend/icons"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { BackendContext } from "@/context"
import { CLIENT_CONTRIBUTION_OWNER_LABELS, CONTRIBUTION_KIND_LABELS, contributionLimitKindLabel } from "@/lib/labels"

export default function ClientContributions() {
  const { client_id } = useLocalSearchParams<{ client_id: string }>()
  const backend = useContext(BackendContext)

  const [groups, setGroups] = useState<ClientContribution[]>()
  const [accounts, setAccounts] = useState<Record<string, Account>>({})
  const [taxYear, setTaxYear] = useState<number>()

  useEffect(() => {
    ;(async () => {
      const clients = backend.collection("clients") as ClientCollection
      const client = clients.model(client_id)
      await client.fetch({ reload: true })

      const contributions = client.link("contributions")?.resolve() as ClientContributionCollection | undefined
      const accountList = client.link("accounts")?.resolve() as AccountCollection | undefined

      await Promise.all([contributions?.fetch({ reload: true }), accountList?.fetch({ reload: true })])

      setAccounts(Object.fromEntries((accountList?.models ?? []).map((a) => [a.id, a])))
      setGroups(contributions?.models ?? [])
    })().catch((err) => {
      console.error("Error: " + err)
      throw err
    })
  }, [backend, client_id])

  const years = useMemo(() => taxYears(groups ?? []), [groups])
  const selected = taxYear ?? defaultTaxYear(years)
  const shown = (groups ?? []).filter((group) => group.attributes.tax_year === selected)

  return (
    <Box className="flex-1 min-h-0 overflow-y-auto p-4">
      {groups == null ? (
        <Spinner size={64} />
      ) : (
        <VStack className="gap-6 max-w-4xl shrink-0">
          <HStack className="items-center justify-between gap-4">
            <Heading size="xl">
              Contributions <Text className="text-sm text-foreground-secondary">{client_id}</Text>
            </Heading>

            {selected == null ? null : (
              <TaxYearSelect
                value={selected}
                years={years}
                onChange={setTaxYear}
              />
            )}
          </HStack>

          {shown.length === 0 ? (
            <Text className="text-foreground-secondary">No contributions on record.</Text>
          ) : (
            shown.map((group) => (
              <GroupSummary
                key={group.id}
                clientID={client_id}
                group={group}
                accounts={accounts}
              />
            ))
          )}
        </VStack>
      )}
    </Box>
  )
}

function TaxYearSelect({
  value,
  years,
  onChange,
}: {
  value: number
  years: number[]
  onChange: (year: number) => void
}) {
  return (
    <Select
      selectedValue={String(value)}
      selectedLabel={String(value)}
      onValueChange={(next: string) => onChange(Number(next))}
    >
      <SelectTrigger
        variant="outline"
        size="sm"
        className="h-20 w-44"
      >
        <SelectInput className="flex-1" />
        <SelectIcon
          as={ChevronDownIcon}
          className="mr-2"
        />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className="bg-transparent">
          {years.map((year) => (
            <SelectItem
              key={year}
              label={String(year)}
              value={String(year)}
            />
          ))}
        </SelectContent>
      </SelectPortal>
    </Select>
  )
}

/** One account type's year: what went in, against what limits, through which accounts. */
function GroupSummary({
  clientID,
  group,
  accounts,
}: {
  clientID: string
  group: ClientContribution
  accounts: Record<string, Account>
}) {
  const { type, subtype, owner, sources } = group.attributes
  const total = sources.reduce((sum, source) => sum + Number(source.amount), 0)
  const limits = group.limits()

  return (
    <VStack className="gap-4 bg-background-secondary rounded-lg border border-border-medium p-4">
      <Heading size="md">
        {subtypeLabel(type, subtype)}{" "}
        <Text className="text-sm text-foreground-secondary">
          {CLIENT_CONTRIBUTION_OWNER_LABELS[owner as keyof typeof CLIENT_CONTRIBUTION_OWNER_LABELS] ?? owner}
        </Text>
      </Heading>

      <VStack className="gap-1">
        {CONTRIBUTION_KIND_LABELS.map(({ kind, title }) => {
          const amount = sources.find((source) => source.kind === kind)?.amount

          return (
            <Row
              key={kind}
              label={title}
              value={amount == null ? "--" : formatMoneyFull(amount)}
            />
          )
        })}
        <Row
          label="Total"
          value={formatMoneyFull(total)}
        />
      </VStack>

      <VStack className="gap-1">
        <Text className="text-foreground-primary font-semibold">Limits</Text>
        {limits.length === 0 ? (
          <Text className="text-foreground-secondary">No annual limit applies.</Text>
        ) : (
          limits.map((limit) => {
            const counted = group.countedToward(limit)
            const remaining = Number(limit.attributes.maximum) - counted

            return (
              <HStack
                key={limit.id}
                className="gap-2 items-start"
              >
                <Link href={`/contribution_limits/${limit.id}`}>
                  <Text className="text-markdown-link hover:opacity-80 underline w-64 shrink-0">
                    {contributionLimitKindLabel(limit.attributes.kind)}
                  </Text>
                </Link>
                <Text className="text-foreground-secondary">
                  {formatMoneyFull(counted)} / {formatMoneyFull(limit.attributes.maximum)}
                </Text>
                <Text className={remaining < 0 ? "text-information-negative" : "text-foreground-tertiary"}>
                  {remaining < 0 ? `${formatMoneyFull(-remaining)} over` : `${formatMoneyFull(remaining)} remaining`}
                </Text>
              </HStack>
            )
          })
        )}
      </VStack>

      <VStack className="gap-1">
        <Text className="text-foreground-primary font-semibold">Accounts</Text>
        {group.contributions().map((year) => (
          <AccountYear
            key={year.id}
            clientID={clientID}
            year={year}
            accounts={accounts}
          />
        ))}
      </VStack>
    </VStack>
  )
}

function AccountYear({
  clientID,
  year,
  accounts,
}: {
  clientID: string
  year: Contribution
  accounts: Record<string, Account>
}) {
  const accountID = accountIDOf(year)
  const account = accountID == null ? undefined : accounts[accountID]
  const name = account ? `${account.attributes.name ?? account.fullType} (${account.attributes.mask})` : accountID
  const total = year.sources().reduce((sum, source) => sum + Number(source.attributes.amount), 0)

  if (accountID == null) {
    return null
  }

  return (
    <HStack className="gap-2 items-start">
      <Link href={`/clients/${clientID}/accounts/${accountID}/contributions/${year.id}`}>
        <Text className="text-markdown-link hover:opacity-80 underline w-64 shrink-0">{name}</Text>
      </Link>
      <Text className="text-foreground-secondary">{formatMoneyFull(total)}</Text>
    </HStack>
  )
}

function Row({ label, value }: { label: string; value: string | undefined }) {
  return (
    <HStack className="gap-2 items-start">
      <Text className="text-foreground-tertiary w-64 shrink-0">{label}</Text>
      <Text className="text-foreground-secondary">{value ?? "--"}</Text>
    </HStack>
  )
}

/** Every tax year the groups cover, newest first. */
function taxYears(groups: ClientContribution[]): number[] {
  return [...new Set(groups.map((group) => group.attributes.tax_year))].sort((a, b) => b - a)
}

/** This year where it is on record, otherwise the newest that is. */
function defaultTaxYear(years: number[]): number | undefined {
  const current = new Date().getFullYear()

  return years.includes(current) ? current : years[0]
}

function subtypeLabel(type: string, subtype: string): string {
  const labels = AccountSubTypes[type as AccountType] as Record<string, string> | undefined

  return labels?.[subtype] ?? subtype
}

/** The account a year belongs to, read off the year's own link. */
function accountIDOf(year: Contribution): string | undefined {
  return year.link("self")?.href.pathname.match(/\/accounts\/([^/]+)\//)?.[1]
}
